import { StorageError } from './supabase/errors'
import { toBase64, encodeObjectName } from './encoding'
import { Http, handleOperation } from './http'
import { DownloadBuilder } from './download-builder'
import {
  FileBody,
  Result,
  UploadOptions,
  UploadResult,
  DownloadOptions,
  ListOptions,
  ListObjectsResponse,
  ObjectInfo,
  FullObject,
  DestinationOptions,
  SignedUrlOptions,
  SignedUrlResult,
  SignedUrlsResultItem,
  CreateSignedUploadUrlResult,
  PublicUrlOptions,
  FetchParameters,
} from './types'

const DEFAULT_UPLOAD_OPTIONS: UploadOptions = {
  cacheControl: '3600',
  contentType: 'text/plain;charset=UTF-8',
  upsert: false,
}

/** 批量删除单次上限。 */
const REMOVE_BATCH_LIMIT = 100
/** 批量签名单次上限。 */
const SIGN_BATCH_LIMIT = 500

/** 从 FormData 中提取出的、服务端支持的元信息字段（值为原始字符串）。 */
interface UploadFormFields {
  cacheControl?: string
  contentType?: string
  metadata?: string
}

/**
 * 单 Bucket 对象操作客户端。
 *
 * 通过 `app.storage.from(bucketId)` 获取。所有 `objectName` 均为 Bucket 内相对路径，
 * 走 `/v1/storages/object/{bucketId}/{name}` 通道，权限由 `storage.objects` / `storage.buckets`
 * 的 RLS 策略判定。
 */
export class StorageFileApi {
  public readonly bucketId: string
  private http: Http
  private shouldThrowOnError: boolean
  /** 实例级自定义请求头，对该实例后续所有请求生效（见 setHeader）。对齐 Supabase 的 `this.headers`。 */
  private headers: Record<string, string> = {}

  constructor(http: Http, bucketId: string, shouldThrowOnError = false) {
    this.http = http
    this.bucketId = bucketId
    this.shouldThrowOnError = shouldThrowOnError
  }

  /** 启用错误抛出模式。开启后请求失败会抛异常；请求成功仍返回 `{ data, error: null }`。 */
  throwOnError(): this {
    this.shouldThrowOnError = true
    return this
  }

  /**
   * 设置自定义请求头（链式）。
   *
   * 对齐 Supabase `BaseApiClient.setHeader`：设置后对**当前实例**后续的所有请求生效。
   * 单次请求内部使用的功能性头（如上传的 `Content-Type` / `x-upsert`）优先级更高，会覆盖此处同名头。
   *
   * 每个 `from(bucketId)` 返回独立实例（持有自定义头的独立副本），互不影响；返回 `this` 以支持链式调用。
   *
   * @param name 请求头名称，非空字符串。
   * @param value 请求头值。
   * @throws 当 name 为空，或 name/value 含 CR/LF（防止请求头注入）时抛 `StorageError`。
   */
  setHeader(name: string, value: string): this {
    if (typeof name !== 'string' || name.trim() === '') {
      throw new StorageError('setHeader requires a non-empty header name')
    }
    if (/[\r\n]/.test(name) || (typeof value === 'string' && /[\r\n]/.test(value))) {
      throw new StorageError('setHeader does not allow CR/LF characters in header name or value')
    }
    // 写时复制：仅改动当前实例自己的头表，对齐 Supabase 语义。
    this.headers = { ...this.headers, [name]: String(value) }
    return this
  }

  /**
   * 直接上传对象，默认不覆盖同名对象。
   *
   * 需要当前用户已登录（携带 Authorization header），适合**服务端或可信客户端**直接上传。
   * 权限由 `storage.objects` 表的 RLS `INSERT` / `UPDATE` 策略判定。
   *
   * 上传行为：
   * - `fileOptions.upsert` 默认 `false`：目标路径已有对象时，上传失败。
   * - `fileOptions.upsert` 设为 `true`：目标路径已有对象时，覆盖写入。
   * - 成功时返回 `{ id, path, fullPath }`，其中 `fullPath` 为 `bucketId/objectName`。
   *
   * 如需让**不受信客户端**（如浏览器前端）上传文件，应改用 `createSignedUploadUrl` + `uploadToSignedUrl` 组合，
   * 服务端先生成临时签名 token，客户端凭 token 上传，无需暴露登录凭证。
   *
   * @param path Bucket 内相对路径。
   * @param fileBody 文件内容。
   * @param fileOptions 上传选项，如 `contentType`、`cacheControl`、`upsert`、`metadata`。
   * @see createSignedUploadUrl uploadToSignedUrl
   */
  async upload(path: string, fileBody: FileBody, fileOptions?: UploadOptions): Promise<Result<UploadResult>> {
    return this.uploadOrUpdate('POST', path, fileBody, fileOptions)
  }

  /**
   * 覆盖更新对象。
   *
   * 与 `upload` 共享同一底层，区别：
   * - 使用 `PUT` 方法（语义为覆盖），不追加 `x-upsert` 头。
   * - 目标路径不存在时也会创建（upsert 行为，与后端实现有关）。
   * - 成功时返回 `{ id, path, fullPath }`。
   *
   * @param path Bucket 内相对路径。
   * @param fileBody 文件内容。
   * @param fileOptions 上传选项，如 `contentType`、`cacheControl`、`metadata`。
   */
  async update(path: string, fileBody: FileBody, fileOptions?: UploadOptions): Promise<Result<UploadResult>> {
    return this.uploadOrUpdate('PUT', path, fileBody, fileOptions)
  }

  /**
   * 下载对象。返回**下载构建器**（thenable），默认 `await` 得到 `Blob`。
   *
   * 需要登录态，权限由 `storage.objects` 的 RLS `SELECT` 策略判定。
   *
   * 两种终态（对齐 Supabase `download().asStream()` 的使用效果）：
   * - 默认：`await bucket.download(path)` → `Result<Blob>`。
   * - 流式：`await bucket.download(path).asStream()` → `Result<ReadableStream>`，适合 Node 大文件等
   *   不希望整体读入内存的场景。
   *
   * 两者统一走原生 `fetch` 的 `Response`：流式终态返回 `Response.body`，Blob 终态调 `Response.blob()`。
   *
   * 下载行为：
   * - 惰性执行：请求在 `await` / `.then()` 时才真正发出。
   * - 返回的 `Blob` 不会自动触发浏览器下载或渲染，需配合 `URL.createObjectURL` 手动处理。
   * - 支持 `parameters.signal`（AbortSignal）取消进行中的下载。
   *
   * @param path Bucket 内相对路径。
   * @param options 下载选项，支持 `download`（触发浏览器下载）、`cacheNonce`（绕过 CDN 缓存）。
   * @param parameters 请求控制参数，支持 `signal` / `cache`。
   * @returns `DownloadBuilder`：可 `await` 得 `Result<Blob>`，或 `.asStream()` 后得 `Result<ReadableStream>`。
   */
  download(
    path: string,
    options?: DownloadOptions,
    parameters?: FetchParameters,
  ): DownloadBuilder {
    return new DownloadBuilder({
      http: this.http,
      bucketId: this.bucketId,
      cleanName: this.cleanPath(path),
      options,
      parameters,
      headers: this.headers,
      shouldThrowOnError: this.shouldThrowOnError,
    })
  }

  /**
   * 获取对象列表（游标分页）。
   *
   * 列出 Bucket 下指定前缀的对象和文件夹。采用**游标分页**，与 Supabase 的 `listV2` 对应。
   *
   * - 首次查询不传 `cursor`，后续用上一页的 `nextCursor` 传入继续翻页。
   * - 返回 `{ folders, objects, hasNext, nextCursor }`（区别于 Supabase `list` 的 offset 分页 `FileObject[]`）。
   * - `path` 不传或为空时列出 Bucket 根目录。
   * - `options.withDelimiter` 为 `true` 时，以 `/` 为分隔符将文件夹和文件分开。
   * - `options.sortBy` 支持按 `name`、`created_at`、`updated_at` 排序。
   *
   * @param path 路径前缀，不传表示 Bucket 根目录。
   * @param options 列表选项，支持 `limit`、`cursor`、`withDelimiter`、`sortBy`。
   * @param parameters 请求控制参数，支持 `signal` / `cache`。
   * @remarks 本方法对应 Supabase 的 `listV2`（游标分页），而非 `list`（offset 分页）。
   */
  async list(path?: string, options?: ListOptions, parameters?: FetchParameters): Promise<Result<ListObjectsResponse>> {
    return handleOperation(async () => {
      const body: Record<string, any> = { prefix: path || '' }
      if (options?.limit !== undefined) body.limit = options.limit
      if (options?.cursor !== undefined) body.cursor = options.cursor
      if (options?.withDelimiter !== undefined) body.with_delimiter = options.withDelimiter
      if (options?.sortBy !== undefined) body.sortBy = options.sortBy

      return this.send<ListObjectsResponse>({
        method: 'POST',
        path: `/object/list/${this.bucketId}`,
        json: body,
        signal: parameters?.signal,
        cache: parameters?.cache,
      })
    }, this.shouldThrowOnError)
  }

  /**
   * 删除一个或多个对象。
   *
   * 权限由 `storage.objects` 的 RLS `DELETE` 策略判定。
   *
   * - 后端单次最多删除 100 个对象，超出时 SDK 自动分批发送。
   * - 返回所有被成功删除的对象信息（`FullObject[]`）。
   *
   * @param paths Bucket 内相对路径数组。
   */
  async remove(paths: string[]): Promise<Result<FullObject[]>> {
    return handleOperation(async () => {
      const names = paths.map(n => this.cleanPath(n))
      const result: FullObject[] = []
      for (let i = 0; i < names.length; i += REMOVE_BATCH_LIMIT) {
        const batch = names.slice(i, i + REMOVE_BATCH_LIMIT)
        const deleted = await this.send<FullObject[]>({
          method: 'DELETE',
          path: `/object/${this.bucketId}`,
          json: { prefixes: batch },
        })
        if (Array.isArray(deleted)) result.push(...deleted)
      }
      return result
    }, this.shouldThrowOnError)
  }

  /**
   * 复制对象，支持跨 Bucket。
   *
   * 权限由 `storage.objects` 的 RLS 策略判定：读取源需要 `SELECT` 权限，写入目标需要 `INSERT` 权限。
   *
   * 复制行为：
   * - `options.destinationBucket` 不传时在同一 Bucket 内复制；传入时跨 Bucket 复制。
   * - `options.upsert` 为 `true` 时，目标路径已有对象则覆盖；否则复制失败。
   * - `options.copyMetadata` 默认为 `undefined`（由服务端决定是否复制原 metadata）。
   *   设为 `false` 并传 `options.metadata` 时，用新 metadata 替换原对象的 metadata。
   * - 返回目标对象的完整路径 `bucketId/objectName`。
   *
   * @param fromPath 源对象 Bucket 内相对路径。
   * @param toPath 目标对象相对路径。
   * @param options 目标 Bucket、覆盖、元数据复制等选项。
   */
  async copy(fromPath: string, toPath: string, options?: DestinationOptions): Promise<Result<{ path: string }>> {
    return handleOperation(async () => {
      const headers = this.buildCopyMoveHeaders(options)
      const data = await this.send<{ Key: string }>({
        method: 'POST',
        path: '/object/copy',
        json: {
          bucketId: this.bucketId,
          sourceKey: this.cleanPath(fromPath),
          destinationKey: this.cleanPath(toPath),
          destinationBucket: options?.destinationBucket,
          copyMetadata: options?.copyMetadata,
          metadata: options?.metadata,
        },
        headers,
      })
      return { path: data.Key }
    }, this.shouldThrowOnError)
  }

  /**
   * 移动对象，支持跨 Bucket；源对象会被删除。
   *
   * 语义为"复制到目标 → 删除源"，非原地重命名。跨 Bucket 时源 Bucket 的对象会被删除。
   * 权限由 `storage.objects` 的 RLS 策略判定：读取源需要 `SELECT`，写入目标需要 `INSERT`，删除源需要 `DELETE`。
   *
   * 移动行为：
   * - `options.destinationBucket` 不传时在同一 Bucket 内移动。
   * - 成功时返回 `{ message: 'Successfully moved' }`。
   *
   * @param fromPath 源对象 Bucket 内相对路径。
   * @param toPath 目标对象相对路径。
   * @param options 目标 Bucket 等选项。
   */
  async move(fromPath: string, toPath: string, options?: DestinationOptions): Promise<Result<{ message: string }>> {
    return handleOperation(async () => {
      const data = await this.send<{ message: string }>({
        method: 'POST',
        path: '/object/move',
        json: {
          bucketId: this.bucketId,
          sourceKey: this.cleanPath(fromPath),
          destinationKey: this.cleanPath(toPath),
          destinationBucket: options?.destinationBucket,
        },
      })
      return { message: data.message || 'Successfully moved' }
    }, this.shouldThrowOnError)
  }

  /**
   * 获取对象元信息。
   *
   * 返回对象在 `storage.objects` 表中的记录信息，包括 `bucketId`、`version`、`etag`、
   * `contentType`、`cacheControl`、`metadata`、`lastModified`、`createdAt` 等。
   *
   * - 对象不存在时返回 `{ data: null, error: StorageError }`。
   *
   * @param path Bucket 内相对路径。
   */
  async info(path: string): Promise<Result<ObjectInfo>> {
    return handleOperation(async () => {
      const cleanName = this.cleanPath(path)
      const data = await this.send<any>({
        method: 'GET',
        path: `/object/info/${this.bucketId}/${this.encodePath(cleanName)}`,
      })
      return {
        id: data.id,
        name: data.name,
        version: data.version,
        bucketId: data.bucket_id,
        size: data.size,
        contentType: data.content_type,
        cacheControl: data.cache_control,
        etag: data.etag,
        metadata: data.metadata,
        lastModified: data.last_modified,
        createdAt: data.created_at,
      }
    }, this.shouldThrowOnError)
  }

  /**
   * 判断对象是否存在。
   *
   * 底层调用 `info` 端点：
   * - 后端返回 HTTP 200 → 存在（返回 `true`）。
   * - 后端返回 HTTP 400 或 404 → 不存在（返回 `false`）。
   * - 其他异常（如 500）→ 返回 `{ data: null, error }`。
   *
   * @param path Bucket 内相对路径。
   */
  async exists(path: string): Promise<Result<boolean>> {
    try {
      const cleanName = this.cleanPath(path)
      await this.send<any>({
        method: 'GET',
        path: `/object/info/${this.bucketId}/${this.encodePath(cleanName)}`,
      })
      return { data: true, error: null }
    } catch (error: any) {
      const status = (error as any)?.status
      if (status !== undefined && [400, 404].includes(Number(status))) {
        return { data: false, error: null }
      }
      if (this.shouldThrowOnError) throw error
      return { data: null, error: error instanceof StorageError ? error : new StorageError(error?.message || String(error)) }
    }
  }

  /**
   * 生成单个签名下载 URL。
   *
   * 需要登录态，权限由 `storage.objects` 的 RLS `SELECT` 策略判定。
   *
   * 签名 URL 行为：
   * - `expiresIn` 以秒为单位，过期后 URL 不可用。
   * - 返回的 `fullSignedURL` 是服务端已拼接好的完整 URL，客户端可直接使用（无需再拼 HOST）。
   * - `options.download` 为 `true` 时在 URL 后追加 `?download`，浏览器访问时自动触发下载；
   *   传字符串时指定下载文件名（`?download=<filename>`）。
   * - 不支持图片 `transform` 转换选项。
   *
   * @param path Bucket 内相对路径。
   * @param expiresIn 过期时间，单位为秒。
   * @param options 签名下载 URL 选项；当前仅支持 `download`，不支持图片 `transform`。
   */
  async createSignedUrl(
    path: string,
    expiresIn: number,
    options?: SignedUrlOptions,
  ): Promise<Result<SignedUrlResult>> {
    return handleOperation(async () => {
      const cleanName = this.cleanPath(path)
      const data = await this.send<{ signedURL?: string; fullSignedURL?: string }>({
        method: 'POST',
        path: `/object/sign/${this.bucketId}/${this.encodePath(cleanName)}`,
        json: { expiresIn },
      })
      const base = data.fullSignedURL || this.http.toFullUrl(data.signedURL || '')
      return { fullSignedURL: this.appendUrlOptions(base, options) }
    }, this.shouldThrowOnError)
  }

  /**
   * 为多个对象批量生成签名下载 URL。
   *
   * 需要登录态，权限由 `storage.objects` 的 RLS `SELECT` 策略判定。
   *
   * 批量签名行为：
   * - 后端单次最多 500 个，超出时 SDK 自动分批发送。
   * - 返回数组中每个元素包含 `path`（请求路径）、`fullSignedURL`（完整签名 URL，生成失败时为 `null`）、`error`。
   * - 部分路径生成失败不会影响其他路径，失败的项 `fullSignedURL` 为 `null`，`error` 为错误信息。
   * - `options.download` 为 `true` 时在 URL 后追加 `?download` 参数。
   *
   * @param paths Bucket 内相对路径数组。
   * @param expiresIn 过期时间，单位为秒。
   * @param options 签名下载 URL 选项；当前仅支持 `download`，不支持图片 `transform`。
   */
  async createSignedUrls(
    paths: string[],
    expiresIn: number,
    options?: SignedUrlOptions,
  ): Promise<Result<SignedUrlsResultItem[]>> {
    return handleOperation(async () => {
      const names = paths.map(n => this.cleanPath(n))
      const result: SignedUrlsResultItem[] = []
      for (let i = 0; i < names.length; i += SIGN_BATCH_LIMIT) {
        const batch = names.slice(i, i + SIGN_BATCH_LIMIT)
        const data = await this.send<Array<{ path: string; signedURL?: string | null; fullSignedURL?: string | null; error?: string | null }>>({
          method: 'POST',
          path: `/object/sign/${this.bucketId}`,
          json: { expiresIn, paths: batch },
        })
        data.forEach((item) => {
          const base = item.fullSignedURL || (item.signedURL ? this.http.toFullUrl(item.signedURL) : null)
          result.push({
            path: item.path,
            fullSignedURL: base ? this.appendUrlOptions(base, options) : null,
            error: item.error ?? null,
          })
        })
      }
      return result
    }, this.shouldThrowOnError)
  }

  /**
   * 两阶段上传（第一阶段）：生成签名上传 URL，需登录态。
   *
   * 服务端基于调用者身份 + RLS 策略判断是否有 `INSERT` 权限，生成临时签名 URL 和 token。
   * 适合的场景：服务端鉴定用户权限后，将 `token` 下发给**不受信客户端**（如浏览器前端），
   * 让客户端用 `uploadToSignedUrl` 直接上传文件，无需暴露登录凭证。
   *
   * 签名上传 URL 行为：
   * - 返回的 `fullSignedURL` 是服务端已拼接好的完整上传 URL，客户端可直接使用。
   * - 返回的 `token` 是临时凭证，与 `uploadToSignedUrl` 配合使用。
   * - `options.upsert` 为 `true` 时，目标路径已有对象时允许覆盖。
   * - 与 `upload` 的区别：
   *   - `upload`：一步完成，需登录态，适合服务端/可信客户端直接上传。
   *   - `createSignedUploadUrl` + `uploadToSignedUrl`：**两阶段上传**——第一阶段需登录态生成 token，
   *     第二阶段无需登录态（凭 token 鉴权），适合浏览器直传、第三方上报等场景。
   *
   * @param path Bucket 内相对路径。
   * @param options 签名上传选项，`upsert=true` 时允许覆盖。
   * @see upload uploadToSignedUrl
   */
  async createSignedUploadUrl(
    path: string,
    options?: { upsert?: boolean },
  ): Promise<Result<CreateSignedUploadUrlResult>> {
    return handleOperation(async () => {
      const cleanName = this.cleanPath(path)
      const headers: Record<string, string> = {}
      if (options?.upsert) headers['x-upsert'] = 'true'

      const data = await this.send<{ url?: string; fullURL?: string; token: string }>({
        method: 'POST',
        path: `/object/upload/sign/${this.bucketId}/${this.encodePath(cleanName)}`,
        headers,
      })
      const fullSignedURL = data.fullURL || this.http.toFullUrl(data.url || '')
      return { fullSignedURL, path: cleanName, token: data.token }
    }, this.shouldThrowOnError)
  }

  /**
   * 两阶段上传（第二阶段）：使用签名上传 token 上传对象，无需登录态。
   *
   * 配合 `createSignedUploadUrl` 完成两阶段上传：
   * 1. 服务端调用 `createSignedUploadUrl(path)` 生成 token（需登录态）
   * 2. 客户端调用本方法，凭 token 上传文件（无需登录态）
   *
   * 上传行为：
   * - 请求**不携带**登录态（`withCredentials: false`），仅凭 `token` 鉴权。
   * - `path` 和 `token` 必须与之前调用 `createSignedUploadUrl` 的返回值对应。
   * - 与 `upload` 的区别：
   *   - `upload`：需登录态，适合服务端/可信客户端。
   *   - `uploadToSignedUrl`：无需登录态，仅凭 `token` 鉴权，适合浏览器直传。
   *
   * @param path Bucket 内相对路径，需与 `createSignedUploadUrl` 的 `path` 一致。
   * @param token `createSignedUploadUrl()` 返回的 `token`。
   * @param fileBody 文件内容。
   * @param fileOptions 上传选项。
   * @see createSignedUploadUrl upload
   */
  async uploadToSignedUrl(
    path: string,
    token: string,
    fileBody: FileBody,
    fileOptions?: UploadOptions,
  ): Promise<Result<{ path: string; fullPath: string }>> {
    return handleOperation(async () => {
      const cleanName = this.cleanPath(path)
      const { body, headers } = this.buildUploadBody(fileBody, fileOptions)

      const data = await this.send<{ Key: string }>({
        method: 'PUT',
        path: `/object/upload/sign/${this.bucketId}/${this.encodePath(cleanName)}`,
        query: { token },
        rawBody: body,
        headers,
        withCredentials: false,
      })
      return { path: cleanName, fullPath: data.Key }
    }, this.shouldThrowOnError)
  }

  /**
   * 获取 public bucket 对象的公开访问 URL（同步，不发请求，不返回 error）。
   *
   * **同步方法**，不发起网络请求，不会抛异常。
   *
   * 公开 URL 行为：
   * - URL 由客户端直接拼接 `{gateway}/v1/storages/object/public/{bucketId}/{path}` 得到。
   * - 拼接过程不会被 RLS 策略拦截，但 URL 是否可访问取决于 Bucket 的 `public` 属性。
   * - `options.download` 为 `true` 时在 URL 后追加 `?download`。
   * - 不支持图片 `transform` 选项。
   *
   * @param path Bucket 内相对路径。
   * @param options 公开 URL 选项；当前仅支持 `download`，不支持图片 `transform`。
   */
  getPublicUrl(path: string, options?: PublicUrlOptions): { data: { publicUrl: string } } {
    const cleanName = this.cleanPath(path)
    const base = this.http.buildUrl(`/object/public/${this.bucketId}/${this.encodePath(cleanName)}`)
    return { data: { publicUrl: this.appendUrlOptions(base, options) } }
  }

  // ---------- 内部工具 ----------

  /**
   * 统一发请求：把实例级自定义头并入每个请求。
   * 单次请求显式传入的功能性头（如 `Content-Type` / `x-upsert`）优先级更高，会覆盖同名自定义头。
   */
  private send<T = any>(opts: Parameters<Http['send']>[0]): Promise<T> {
    return this.http.send<T>({ ...opts, headers: { ...this.headers, ...(opts.headers || {}) } })
  }

  private async uploadOrUpdate(
    method: 'POST' | 'PUT',
    path: string,
    fileBody: FileBody,
    fileOptions?: UploadOptions,
  ): Promise<Result<UploadResult>> {
    return handleOperation(async () => {
      const cleanName = this.cleanPath(path)
      const { body, headers } = this.buildUploadBody(fileBody, fileOptions)

      if (method === 'POST') {
        // x-upsert 不放进 FormData：它是请求级行为开关而非对象元信息，服务端需在解析 body 前读到；
        // 且 binary 直传路径没有表单字段可放，统一走 header 以保持两条上传路径一致。
        headers['x-upsert'] = String(Boolean(fileOptions?.upsert))
      }

      const data = await this.send<{ Id: string; Key: string }>({
        method,
        path: `/object/${this.bucketId}/${this.encodePath(cleanName)}`,
        rawBody: body,
        headers,
      })

      return { id: data.Id, path: cleanName, fullPath: data.Key }
    }, this.shouldThrowOnError)
  }

  private cleanPath(path: string): string {
    const clean = path.replace(/^\/+|\/+$/g, '').replace(/\/+/g, '/')
    // 客户端纵深防御：拒绝 `..` 段，避免 URL 规范化后逃出 bucket 路径前缀（路径穿越）。
    // encodeURIComponent('..') === '..'，无法靠编码消除，必须显式拦截。
    if (clean.split('/').some(seg => seg === '..')) {
      throw new StorageError('invalid path: ".." segment is not allowed')
    }
    return clean
  }

  /** 把 `cleanPath` 后的对象名编码为可安全拼进 URL path 的形式（保留 `/` 分隔符）。 */
  private encodePath(cleanName: string): string {
    return encodeObjectName(cleanName)
  }

  private buildUploadBody(
    fileBody: FileBody,
    fileOptions?: UploadOptions,
  ): { body: any; headers: Record<string, string> } {
    const headers: Record<string, string> = { ...(fileOptions?.headers || {}) }
    let body: any = fileBody

    // 与 supabase-js 一致的按类型分流策略：
    // - Blob / File → multipart/form-data：cacheControl / contentType / metadata 走表单字段，
    //   Content-Type 为 CORS 安全值，不触发自定义头预检（浏览器最常见的文件上传场景）。
    // - FormData → 取其中的文件内容，并允许携带服务端支持的元信息字段；同样走 multipart 表单。
    // - 其它（string / ArrayBuffer / 二进制视图 / 流等）→ Binary 直传：元信息走请求头。
    //   该路径会发送 cache-control / x-metadata 等自定义头，浏览器下需要网关在
    //   Access-Control-Allow-Headers 放行，否则触发 CORS 预检失败。
    const isFormData = typeof FormData !== 'undefined' && fileBody instanceof FormData
    const isBlob = typeof Blob !== 'undefined' && fileBody instanceof Blob
    if (isBlob || isFormData) {
      let file: Blob
      let fields: UploadFormFields = {}
      if (isFormData) {
        const extracted = this.extractFromFormData(fileBody as FormData)
        file = extracted.file
        fields = extracted.fields
      } else {
        file = fileBody as Blob
      }

      // 元信息取值优先级：fileOptions（显式传入）> FormData 携带 > 默认值。
      const cacheControl = fileOptions?.cacheControl ?? fields.cacheControl ?? DEFAULT_UPLOAD_OPTIONS.cacheControl
      const contentType = fileOptions?.contentType ?? fields.contentType
      const metadata = fileOptions?.metadata !== undefined ? JSON.stringify(fileOptions.metadata) : fields.metadata

      const form = new FormData()
      if (cacheControl !== undefined) form.append('cacheControl', String(cacheControl))
      // contentType 不落默认值：Blob / File 自带 MIME 类型，强制写默认值反而会覆盖真实类型。
      if (contentType !== undefined) form.append('contentType', contentType)
      if (metadata !== undefined) form.append('metadata', metadata)
      // file 放在最后：服务端边解析 multipart 边落库，要求文件部分排在所有元信息字段之后，
      // 先拿到 cacheControl / metadata 才能写库。
      form.append('', file)
      body = form
    } else {
      body = fileBody
      const cacheControl = fileOptions?.cacheControl ?? DEFAULT_UPLOAD_OPTIONS.cacheControl
      const contentType = fileOptions?.contentType ?? DEFAULT_UPLOAD_OPTIONS.contentType
      headers['cache-control'] = `max-age=${cacheControl}`
      if (contentType) headers['content-type'] = contentType
      if (fileOptions?.metadata) headers['x-metadata'] = this.toBase64(JSON.stringify(fileOptions.metadata))
    }

    return { body, headers }
  }

  /**
   * 从用户传入的上传 FormData 中提取文件内容，以及服务端支持的元信息字段。
   *
   * - 文件部分：单个对象上传只对应一个文件，缺少或多于一个均抛错。
   * - 元信息字段：服务端仅支持 `cacheControl` / `contentType` / `metadata`，这几个会被取出（其值仍可被
   *   `fileOptions` 覆盖，覆盖逻辑在调用方）；其余不支持的字段对上传无意义，宽松忽略并打印一次告警。
   *
   * @param source 用户传入的原始 FormData。
   */
  private extractFromFormData(source: FormData): { file: Blob; fields: UploadFormFields } {
    const fields: UploadFormFields = {}
    let file: Blob | undefined
    let fileCount = 0
    const ignoredFields: string[] = []
    source.forEach((value, key) => {
      // File 是 Blob 的子类，二进制内容统一按文件部分处理。
      if (typeof Blob !== 'undefined' && value instanceof Blob) {
        file = value
        fileCount += 1
        return
      }
      if (key === 'cacheControl' || key === 'contentType' || key === 'metadata') {
        fields[key] = String(value)
        return
      }
      ignoredFields.push(key)
    })

    if (ignoredFields.length > 0) {
      console.warn(`Ignored unsupported FormData field(s): [${ignoredFields.join(', ')}]. Only file, cacheControl, contentType and metadata are supported`)
    }

    if (fileCount === 0 || !file) {
      throw new StorageError('FormData must contain a file')
    }
    if (fileCount > 1) {
      throw new StorageError('FormData must contain exactly one file')
    }
    return { file, fields }
  }

  private buildCopyMoveHeaders(options?: DestinationOptions): Record<string, string> {
    const headers: Record<string, string> = {}
    if (options?.upsert) headers['x-upsert'] = 'true'
    if (options?.copyMetadata === false && options?.metadata) {
      headers['x-metadata'] = this.toBase64(JSON.stringify(options.metadata))
    }
    return headers
  }

  private appendUrlOptions(url: string, options?: SignedUrlOptions | PublicUrlOptions): string {
    const params: string[] = []
    if (options?.download !== undefined) {
      const value = options.download === true ? '' : String(options.download)
      params.push(`download=${encodeURIComponent(value)}`)
    }
    if (options?.cacheNonce !== undefined) {
      params.push(`cacheNonce=${encodeURIComponent(String(options.cacheNonce))}`)
    }

    if (params.length === 0) return url
    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}${params.join('&')}`
  }

  private toBase64(data: string): string {
    return toBase64(data)
  }
}
